import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Radio, Switch, Checkbox, Spin, Divider, Tabs } from 'antd';
import { toast } from 'react-hot-toast';
import { IEmailSettings, AltisEmailSendProtocol, IGeneralSettings } from '../../interfaces/settings';
import { IPageData, IPageProps } from '../../interfaces/page-data';
import HTTPService from '../../utils/makerequest';

const GeneralSettingsForm: React.FC<{ data: IGeneralSettings; onSaveSuccess: () => void }> = ({ data, onSaveSuccess }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        jwtToken: data.jwtToken || '',
        imageBaseURL: data.imageBaseURL || 'http://localhost:7010/image/',
      });
    }
  }, [data, form]);

  const onFinish = (values: any) => {
    const payload: IGeneralSettings = {
      jwtToken: values.jwtToken,
      imageBaseURL: values.imageBaseURL,
    };

    HTTPService.PUT('/Settings/generalSettings', payload)
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          toast.success('Genel ayarlar başarıyla güncellendi.');
          onSaveSuccess();
        } else {
          toast.error('Genel ayarlar kaydedilirken hata oluştu.');
        }
      })
      .catch((error) => {
        toast.error('Kaydetme hatası: ' + (error?.message || 'Bir hata oluştu.'));
      });
  };

  return (
    <Form form={form} layout='vertical' onFinish={onFinish}>
      <Form.Item
        label='JWT Gizli Anahtarı (jwtToken)'
        name='jwtToken'
        rules={[{ required: true, message: 'Lütfen JWT gizli anahtarını giriniz!' }]}
      >
        <Input.Password placeholder='JWT Secret Key' />
      </Form.Item>

      <Form.Item
        label='Görsel Sunucu URL (imageBaseURL)'
        name='imageBaseURL'
        rules={[{ required: true, message: 'Lütfen Görsel Sunucu URL adresini giriniz!' }]}
        help='Örnek: http://localhost:7010/image/'
      >
        <Input placeholder='http://localhost:7010/image/' />
      </Form.Item>

      <div className='d-flex justify-content-end mt-4'>
        <Button type='primary' htmlType='submit' size='large'>
          Kaydet
        </Button>
      </div>
    </Form>
  );
};

const SettingsForm: React.FC<{ data: IEmailSettings; onSaveSuccess: () => void }> = ({ data, onSaveSuccess }) => {
  const [form] = Form.useForm();
  const [protocol, setProtocol] = useState<AltisEmailSendProtocol>(
    data?.sendProtocol !== undefined ? Number(data.sendProtocol) : AltisEmailSendProtocol.SMTP
  );

  useEffect(() => {
    if (data) {
      setProtocol(data.sendProtocol !== undefined ? Number(data.sendProtocol) : AltisEmailSendProtocol.SMTP);
      form.setFieldsValue({
        mailIsActive: data.mailIsActive ?? false,
        sendProtocol: data.sendProtocol !== undefined ? Number(data.sendProtocol) : AltisEmailSendProtocol.SMTP,
        smtpConfig: {
          credentialsEmailAddress: data.smtpConfig?.credentialsEmailAddress || '',
          emailAddress: data.smtpConfig?.emailAddress || '',
          emailPass: data.smtpConfig?.emailPass || '',
          emailHost: data.smtpConfig?.emailHost || '',
          emailPort: data.smtpConfig?.emailPort || 587,
          emailUseDefaultCredentials: data.smtpConfig?.emailUseDefaultCredentials ?? false,
          emailEnableSsl: data.smtpConfig?.emailEnableSsl ?? true,
        },
        graphConfig: {
          credentialsEmailAddress: data.graphConfig?.credentialsEmailAddress || '',
          azureTenantId: data.graphConfig?.azureTenantId || '',
          azureClientId: data.graphConfig?.azureClientId || '',
          azureClientSecret: data.graphConfig?.azureClientSecret || '',
          serviceURL: data.graphConfig?.serviceURL || 'https://graph.microsoft.com/.default',
        },
      });
    }
  }, [data, form]);

  const onFinish = (values: any) => {
    const payload: IEmailSettings = {
      mailIsActive: values.mailIsActive,
      sendProtocol: Number(values.sendProtocol),
      smtpConfig: {
        credentialsEmailAddress: values.smtpConfig?.credentialsEmailAddress ?? data?.smtpConfig?.credentialsEmailAddress ?? '',
        emailAddress: values.smtpConfig?.emailAddress ?? data?.smtpConfig?.emailAddress ?? '',
        emailPass: values.smtpConfig?.emailPass ?? '',
        emailHost: values.smtpConfig?.emailHost ?? data?.smtpConfig?.emailHost ?? '',
        emailPort: values.smtpConfig?.emailPort !== undefined && values.smtpConfig?.emailPort !== null && !isNaN(Number(values.smtpConfig?.emailPort))
          ? Number(values.smtpConfig.emailPort)
          : (data?.smtpConfig?.emailPort || 587),
        emailUseDefaultCredentials: values.smtpConfig?.emailUseDefaultCredentials !== undefined
          ? Boolean(values.smtpConfig.emailUseDefaultCredentials)
          : (data?.smtpConfig?.emailUseDefaultCredentials ?? false),
        emailEnableSsl: values.smtpConfig?.emailEnableSsl !== undefined
          ? Boolean(values.smtpConfig.emailEnableSsl)
          : (data?.smtpConfig?.emailEnableSsl ?? true),
      },
      graphConfig: {
        credentialsEmailAddress: values.graphConfig?.credentialsEmailAddress ?? data?.graphConfig?.credentialsEmailAddress ?? '',
        azureTenantId: values.graphConfig?.azureTenantId ?? data?.graphConfig?.azureTenantId ?? '',
        azureClientId: values.graphConfig?.azureClientId ?? data?.graphConfig?.azureClientId ?? '',
        azureClientSecret: values.graphConfig?.azureClientSecret ?? '',
        serviceURL: values.graphConfig?.serviceURL ?? data?.graphConfig?.serviceURL ?? 'https://graph.microsoft.com/.default',
      },
    };

    HTTPService.PUT('/Settings/emailSettings', payload)
      .then((res: any) => {
        if (res.status >= 200 && res.status < 300) {
          toast.success('E-posta ayarları başarıyla güncellendi.');
          onSaveSuccess();
        } else {
          toast.error(res?.errorText || 'Ayarlar kaydedilirken bir hata oluştu.');
        }
      })
      .catch((error: any) => {
        toast.error('Kaydetme hatası: ' + (error?.errorText || error?.message || 'Bir hata oluştu.'));
      });
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{
        mailIsActive: data?.mailIsActive ?? false,
        sendProtocol: data?.sendProtocol !== undefined ? Number(data.sendProtocol) : AltisEmailSendProtocol.SMTP,
      }}
    >
      <Form.Item label='E-Posta Gönderimi Durumu' name='mailIsActive' valuePropName='checked'>
        <Switch checkedChildren='Aktif' unCheckedChildren='Pasif' />
      </Form.Item>

      <Form.Item label='E-Posta Gönderim Protokolü' name='sendProtocol'>
        <Radio.Group onChange={(e) => setProtocol(e.target.value)} value={protocol}>
          <Radio.Button value={AltisEmailSendProtocol.SMTP}>SMTP Server</Radio.Button>
          <Radio.Button value={AltisEmailSendProtocol.AzureGraph}>Azure Graph API</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Divider />

      {protocol === AltisEmailSendProtocol.SMTP ? (
        <>
          <h4>SMTP Konfigürasyonu</h4>
          <Form.Item
            label='Kimlik E-Posta Adresi'
            name={['smtpConfig', 'credentialsEmailAddress']}
            rules={[
              { required: true, message: 'Lütfen kimlik e-postası giriniz!' },
              { type: 'email', message: 'Geçerli bir e-posta adresi giriniz!' },
            ]}
          >
            <Input placeholder='ornek@domain.com' />
          </Form.Item>

          <Form.Item
            label='Gönderen E-Posta Adresi'
            name={['smtpConfig', 'emailAddress']}
            rules={[
              { required: true, message: 'Lütfen e-posta adresi giriniz!' },
              { type: 'email', message: 'Geçerli bir e-posta adresi giriniz!' },
            ]}
          >
            <Input placeholder='ornek@domain.com' />
          </Form.Item>

          <Form.Item
            label='E-Posta Parolası'
            name={['smtpConfig', 'emailPass']}
            help='Mevcut şifreyi korumak için boş bırakınız.'
          >
            <Input.Password placeholder='•••••••• (Gizlidir)' />
          </Form.Item>

          <Form.Item
            label='SMTP Host (Sunucu)'
            name={['smtpConfig', 'emailHost']}
            rules={[{ required: true, message: 'Lütfen SMTP host giriniz!' }]}
          >
            <Input placeholder='smtp.office365.com' />
          </Form.Item>

          <Form.Item
            label='SMTP Port'
            name={['smtpConfig', 'emailPort']}
            rules={[{ required: true, type: 'number', min: 1, max: 65535, message: 'Lütfen geçerli bir port giriniz!' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder='587' />
          </Form.Item>

          <Form.Item name={['smtpConfig', 'emailUseDefaultCredentials']} valuePropName='checked'>
            <Checkbox>Varsayılan Kimlik Bilgilerini Kullan</Checkbox>
          </Form.Item>

          <Form.Item name={['smtpConfig', 'emailEnableSsl']} valuePropName='checked'>
            <Checkbox>SSL / TLS Kullan</Checkbox>
          </Form.Item>
        </>
      ) : (
        <>
          <h4>Azure Graph API Konfigürasyonu</h4>
          <Form.Item
            label='Gönderen E-Posta Adresi (Credentials Email)'
            name={['graphConfig', 'credentialsEmailAddress']}
            rules={[
              { required: true, message: 'Lütfen e-posta adresi giriniz!' },
              { type: 'email', message: 'Geçerli bir e-posta adresi giriniz!' },
            ]}
          >
            <Input placeholder='ornek@domain.com' />
          </Form.Item>

          <Form.Item
            label='Azure Tenant ID'
            name={['graphConfig', 'azureTenantId']}
            rules={[{ required: true, message: 'Lütfen Tenant ID giriniz!' }]}
          >
            <Input placeholder='00000000-0000-0000-0000-000000000000' />
          </Form.Item>

          <Form.Item
            label='Azure Client ID'
            name={['graphConfig', 'azureClientId']}
            rules={[{ required: true, message: 'Lütfen Client ID giriniz!' }]}
          >
            <Input placeholder='00000000-0000-0000-0000-000000000000' />
          </Form.Item>

          <Form.Item
            label='Azure Client Secret'
            name={['graphConfig', 'azureClientSecret']}
            help='Mevcut secret değerini korumak için boş bırakınız.'
          >
            <Input.Password placeholder='Secret Key (Gizlidir)' />
          </Form.Item>

          <Form.Item
            label='Service URL'
            name={['graphConfig', 'serviceURL']}
            rules={[{ required: true, message: 'Lütfen Service URL giriniz!' }]}
          >
            <Input placeholder='https://graph.microsoft.com/.default' />
          </Form.Item>
        </>
      )}

      <div className='d-flex justify-content-end mt-4'>
        <Button type='primary' htmlType='submit' size='large'>
          Kaydet
        </Button>
      </div>
    </Form>
  );
};

const PageSettings: React.FunctionComponent<IPageProps> = (props) => {
  const { onSetPage } = props;
  const [loading, setLoading] = useState(true);
  const [emailSettings, setEmailSettings] = useState<IEmailSettings>({
    mailIsActive: false,
    sendProtocol: AltisEmailSendProtocol.SMTP,
  });
  const [generalSettings, setGeneralSettings] = useState<IGeneralSettings>({
    jwtToken: '',
    imageBaseURL: 'http://localhost:7010/image/',
  });

  const pageData: IPageData = {
    title: 'Settings',
    loaded: true,
    breadcrumbs: [
      {
        title: 'Home',
        route: 'dashboard',
      },
      {
        title: 'Settings',
        route: 'settings',
      },
    ],
  };

  useEffect(() => {
    onSetPage(pageData);
    getAllSettings();
  }, []);

  const getAllSettings = () => {
    setLoading(true);
    Promise.all([
      HTTPService.GET('/Settings/emailSettings'),
      HTTPService.GET('/Settings/generalSettings'),
    ])
      .then(([emailRes, generalRes]) => {
        if (emailRes?.data) {
          setEmailSettings(emailRes.data);
        }
        if (generalRes?.data) {
          setGeneralSettings(generalRes.data);
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error('Ayarlar yüklenirken hata oluştu.');
        setLoading(false);
      });
  };

  return (
    <>
      <Alert
        className='mb-4'
        message='Sistem Ayarları'
        type='info'
        showIcon
        description='Sistem genel konfigürasyonlarını (JWT anahtarı, Görsel URL) ve E-Posta ayarlarını veritabanı üzerinden buradan yönetebilirsiniz.'
      />
      <Card title='Sistem Ayarları'>
        {loading ? (
          <div className='text-center py-5'>
            <Spin size='large' />
          </div>
        ) : (
          <Tabs
            defaultActiveKey='general'
            items={[
              {
                key: 'general',
                label: 'Genel Ayarlar',
                children: <GeneralSettingsForm data={generalSettings} onSaveSuccess={getAllSettings} />,
              },
              {
                key: 'email',
                label: 'E-Posta Ayarları',
                children: <SettingsForm data={emailSettings} onSaveSuccess={getAllSettings} />,
              },
            ]}
          />
        )}
      </Card>
    </>
  );
};

export default PageSettings;
